<template>
  <div>
    <!-- Hero Section -->
    <section class="bg-gradient-to-br from-primary-600 to-primary-800 text-white section-padding">
      <div class="container-custom text-center">
        <h1 class="text-4xl md:text-5xl font-bold mb-6">
          About CW Home Improvements
        </h1>
        <p class="text-xl text-primary-100 max-w-3xl mx-auto">
          Your trusted partner in home maintenance and repairs for over 5 years. 
          We're committed to quality workmanship and exceptional customer service.
        </p>
      </div>
    </section>

    <!-- Company Story -->
    <section class="section-padding bg-white">
      <div class="container-custom">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Our Story
            </h2>
            <div class="space-y-4 text-lg text-gray-600">
              <p>
                Founded in 2020, CW Home Improvements began as a small family business with a simple mission: 
                to provide reliable, quality home improvement services to our community. What started as 
                a one-man operation has grown into a trusted team of skilled professionals.
              </p>
              <p>
                Over the years, we've helped hundreds of homeowners maintain and improve their 
                properties. From simple repairs to complex renovations, we've built our reputation 
                on honesty, reliability, and exceptional workmanship.
              </p>
              <p>
                Today, we're proud to serve with a comprehensive 
                range of home services, always maintaining the personal touch and attention to 
                detail that our customers have come to expect.
              </p>
            </div>
          </div>
          <div class="relative">
            <div class="bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl p-8">
              <div class="grid grid-cols-2 gap-6">
                <div class="text-center">
                  <div class="text-3xl font-bold text-primary-800">5+</div>
                  <div class="text-sm text-primary-700">Years Experience</div>
                </div>
                <div class="text-center">
                  <div class="text-3xl font-bold text-primary-800">100+</div>
                  <div class="text-sm text-primary-700">Happy Customers</div>
                </div>
                <div class="text-center">
                  <div class="text-3xl font-bold text-primary-800">100+</div>
                  <div class="text-sm text-primary-700">Projects Completed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Our Values -->
    <section class="section-padding gradient-bg">
      <div class="container-custom">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Our Values
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            These core values guide everything we do and ensure we deliver the best possible service to our customers.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div class="text-center">
            <div class="w-16 h-16 bg-primary-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Quality</h3>
            <p class="text-gray-600">
              We never compromise on quality. Every job is completed to the highest standards with attention to detail.
            </p>
          </div>

          <div class="text-center">
            <div class="w-16 h-16 bg-primary-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Reliability</h3>
            <p class="text-gray-600">
              You can count on us to show up on time, complete the job as promised, and stand behind our work.
            </p>
          </div>

          <div class="text-center">
            <div class="w-16 h-16 bg-primary-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Integrity</h3>
            <p class="text-gray-600">
              Honest pricing, transparent communication, and ethical business practices in everything we do.
            </p>
          </div>

          <div class="text-center">
            <div class="w-16 h-16 bg-primary-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
              </svg>
            </div>
            <h3 class="text-xl font-semibold text-gray-900 mb-3">Care</h3>
            <p class="text-gray-600">
              We treat your home like our own, with respect for your property and consideration for your needs.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="section-padding bg-primary-800 text-white">
      <div class="container-custom text-center">
        <h2 class="text-3xl md:text-4xl font-bold mb-6">
          Ready to Work With Us?
        </h2>
        <p class="text-xl mb-8 text-primary-100 max-w-2xl mx-auto">
          Join hundreds of satisfied customers who trust CW Home Improvements for their home maintenance and repair needs.
        </p>
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
                      <a href="tel:5613983356" class="btn-secondary bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary-600">
            Call Now: (561) 398-3356
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'About'
}
</script> 